import { Button, Divider, Link, Stack } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { useEffect } from "react";
import {
  ActionItemContextProperty,
  ActionItemFeed,
  ActionItemProperties,
  IssueDrawerContent,
  ITeamSection,
  ItemDescriptionField,
  ItemDrawerTitle,
  SignalRGroup,
  SnackbarContent,
  TaskItemDrawerContent,
  useDrawer,
} from "..";
import { ActionItemPropertyType, Links, SupportingItemType } from "../../constants";
import { useSignalR } from "../../hooks";
import { queryKeys, useEditActionItem, useGetActionItemDetail, UserDto } from "../../http";
import { ITeamSummary } from "../../models";

interface IProps {
  actionItemGuid: string;
  onOwnershipChanged?: (actionItemGuid: string, actionItemDescription: string, removedOwners: UserDto[]) => void;
}

const ActionItemDrawerContent = (props: IProps) => {
  const { isLoading, data: actionItem } = useGetActionItemDetail(props.actionItemGuid);
  const { mutate: editActionItem } = useEditActionItem();
  const { showDrawer, closeDrawer } = useDrawer();
  const { enqueueSnackbar } = useSnackbar();
  const signalR = useSignalR();
  const queryClient = useQueryClient();

  useEffect(() => {
    signalR.joinGroup(SignalRGroup.ActionItem(props.actionItemGuid));

    return () => {
      signalR.leaveGroup(SignalRGroup.ActionItem(props.actionItemGuid));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.actionItemGuid]);

  useEffect(() => {
    const handleShouldRefetchActionItem = (actionItemGuid: string) => {
      queryClient.invalidateQueries(queryKeys.actionItems.getActionItemDetail(actionItemGuid));
    };

    signalR.onShouldRefetchActionItem(handleShouldRefetchActionItem);
    return () => signalR.offShouldRefetchActionItem(handleShouldRefetchActionItem);
  }, [queryClient, signalR]);

  function handleDescriptionChanged(value: string) {
    if (actionItem === undefined) {
      return;
    }

    editActionItem({
      actionItemGuid: actionItem.guid,
      description: value,
      planGuid: actionItem.teamPlanSection.planGuid,
    });
  }

  function handleTeamChanged(selectedTeam: ITeamSummary | null, selectedSection: ITeamSection | null) {
    if (actionItem === undefined) {
      return;
    }

    if (!selectedSection) {
      enqueueSnackbar(<SnackbarContent title="Item removed from team." />, {
        action: (
          <Button href={Links.ActionItemDetail(actionItem.guid)} sx={{ whiteSpace: "nowrap" }}>
            Open Page
          </Button>
        ),
      });
      closeDrawer();
    } else if (actionItem.teamPlanSection.teamSlug !== selectedTeam?.slug) {
      enqueueSnackbar(
        <SnackbarContent
          title={
            <>
              <span>Item moved to </span>
              <Link onClick={(e) => e.stopPropagation()} href={Links.TeamStrategicMap(selectedTeam!.slug)}>
                {selectedTeam!.name}
              </Link>
              .
            </>
          }
        />,
        {
          action: (
            <Button href={Links.ActionItemDetail(actionItem.guid)} sx={{ whiteSpace: "nowrap" }}>
              Open Page
            </Button>
          ),
        }
      );
      closeDrawer();
    }
  }

  function getDrawerTitleForSupportingItem(itemType: SupportingItemType, itemGuid: string): React.ReactNode {
    switch (itemType) {
      case SupportingItemType.ActionItem:
        return <ItemDrawerTitle link={Links.ActionItemDetail(props.actionItemGuid)} />;
      case SupportingItemType.TaskItem:
        return <ItemDrawerTitle link={Links.TaskItemDetail(itemGuid)} />;
      case SupportingItemType.Issue:
        return <ItemDrawerTitle link={Links.IssueDetail(itemGuid)} />;
      default:
        return null;
    }
  }

  function getDrawerContentForSupportingItem(itemType: SupportingItemType, itemGuid: string): React.ReactNode {
    switch (itemType) {
      case SupportingItemType.ActionItem:
        return <ActionItemDrawerContent actionItemGuid={itemGuid} />;
      case SupportingItemType.TaskItem:
        return <TaskItemDrawerContent taskItemGuid={itemGuid} />;
      case SupportingItemType.Issue:
        return <IssueDrawerContent issueGuid={itemGuid} />;
      default:
        return null;
    }
  }

  function showSupportingItemDrawer(itemType: SupportingItemType, itemGuid: string) {
    showDrawer({
      title: getDrawerTitleForSupportingItem(itemType, itemGuid),
      content: getDrawerContentForSupportingItem(itemType, itemGuid),
    });
  }

  return (
    <>
      <Stack sx={{ px: { xs: 0.5, sm: 1 }, py: 0.5 }}>
        {!isLoading && (actionItem?.context || actionItem?.userCanEdit) && (
          <ActionItemContextProperty actionItem={actionItem} isEditable={actionItem.userCanEdit} />
        )}

        <ItemDescriptionField
          isLoading={isLoading}
          isEditable={actionItem?.userCanEdit ?? false}
          value={actionItem?.description}
          onChange={handleDescriptionChanged}
          variant={"h5"}
        />
      </Stack>
      <Divider />
      <Stack divider={<Divider flexItem />} sx={{ overflow: "auto" }}>
        <ActionItemProperties
          isLoading={isLoading}
          actionItem={actionItem}
          propertyVisibility={{
            [ActionItemPropertyType.Notes]: true,
            [ActionItemPropertyType.Ownership]: true,
            [ActionItemPropertyType.Status]: true,
            [ActionItemPropertyType.DueDate]: true,
            [ActionItemPropertyType.SupportingItems]: actionItem?.teamPlanSection != null,
            [ActionItemPropertyType.Metrics]: true,
            [ActionItemPropertyType.AzureDevOps]: true,
          }}
          onTeamChanged={handleTeamChanged}
          onOwnershipChanged={props.onOwnershipChanged}
          onSupportingItemClicked={showSupportingItemDrawer}
        />
        <ActionItemFeed actionItemGuid={props.actionItemGuid} />
      </Stack>
    </>
  );
};

export { ActionItemDrawerContent };
